/* @flow */

import React, {Component} from 'react';
import MessageBox from '../../MessageBox/MessageBox';
import DownloadIcon from '@react/react-spectrum/Icon/Download';
import DownloadUtil from '../../../../../utils/DownloadUtil';
import KaizenViewScss from './KaizenView.scss';
import DeviceDetectionUtil from '../../../../../utils/DeviceDetectionUtil';
import AppsUtil from '../../../../../utils/AppsUtil';
import CSSUtil from '../../../../../utils/CSSUtil';
import {getHeadingTextId, getBodyTextId, WORKFLOWS} from './../stringUtil';
import {DISPLAY_MESSAGE_LAG, DOWNLOAD_CONTROLLER_USER_SOURCE} from '../../../../../constants/AppPicker/appPickerConstants';

type Props = {
	className: ?string,
	source: string,
	displayCoachMark: Function,
	handleDownloadEvent: Function,
	handleAnalyticsEvent: Function,
	onGetHelp: Function,
	downloadViaCCD: boolean,
	product: Object,
	intl: Object,
	messagesList: Array<Object>
};

type State = {
	showToasts: boolean
};

export default class KaizenView extends Component<Props, State> {
	productDetails: Object;

	constructor(props: Props) {
		super(props);
		this.productDetails = this.props.product;
		this.state = {
			showToasts: !(DeviceDetectionUtil.isEdgeOrIE() || DeviceDetectionUtil.isFirefox())
		};
	}

	/**
	 * Executed when the download coach marks are hidden
	 *
	 * @memberof KaizenView
	 */
	onHideCallback = () => {
		setTimeout(() => {
			if (!this.state.showToasts) {
				// Update state and add animation only if the toasts are hidden, i.e., showToasts is false
				this.setState({showToasts: true}, () => {
					this.props.messagesList.forEach((message) => {
						CSSUtil.addClass(document.getElementById(message.variant), 'message-box-fade-in');
					});
				});
			}
		}, DISPLAY_MESSAGE_LAG);
	}

	componentDidMount() {
		this.props.displayCoachMark(false, this.onHideCallback);
	}

	onRestartDownload = (e: Object) => {
		e.preventDefault();
		const isLink = e.target.tagName === 'A';
		DownloadUtil.downloadProduct({
			sapcode: this.productDetails.sapCode,
			isAllApps: this.props.downloadViaCCD,
			hasThor: false
		}).then(() => {
			this.props.handleDownloadEvent(true);
		});
		this.props.displayCoachMark(true, this.onHideCallback);
		this.props.handleAnalyticsEvent({
			eventObj: {
				type: 'click',
				subtype: 'restart-download'
			},
			contentObj: {
				category: isLink ? 'Link' : 'Icon'
			}
		});
	};

	getDownloadFileName(sapCode: string) {
		return this.props.downloadViaCCD ? DownloadUtil.getDownloadFileName('KCCC') : DownloadUtil.getDownloadFileName(sapCode);
	}

	renderHeaderText = () => (
		<div data-cs-mask='' className={KaizenViewScss.title}>
			{this.props.intl.formatMessage(
				{id: getHeadingTextId(WORKFLOWS.KAIZEN, this.props.source, this.productDetails.sapCode)},
				{
					appName: this.productDetails.name,
					userName : AppsUtil.getImsData().name
				}
			)}
		</div>
	);

	renderBodyText = () => {
		let appname = this.productDetails.name;
		// Remove this in future if requirements do not change.
		if (appname === null) {
			appname = '{firstapp}';
		}
		return (
			<div className={`${KaizenViewScss.body} ${(this.props.source === DOWNLOAD_CONTROLLER_USER_SOURCE.LOGGED_OUT_CAMPAIGN_PAGE) ? KaizenViewScss.loggedOutCampaign : ''}`}>
				<div className={KaizenViewScss.productBody}>
					<div className={KaizenViewScss.shallowInstructions}>
						{this.props.intl.formatMessage(
							{
								id: getBodyTextId(WORKFLOWS.KAIZEN, this.props.source, this.productDetails.sapCode, false)
							},
							{
								appname,
								filename: this.getDownloadFileName(this.productDetails.sapCode)
							}
						)}
					</div>
				</div>
				{this.renderHelpSection()}
			</div>
		);
	};

	renderHelpSection = () => (
		<div className={KaizenViewScss.helpSection}>
			<span className={KaizenViewScss.tryAgainTitle}>
				{this.props.intl.formatMessage({id: 'apps.allAppsAppPicker.kaizen.tryAgainTitle'})}
			</span>
			<a className={KaizenViewScss.tryAgainLink} href='#' target='download_frame' onClick={this.onRestartDownload}>
				{this.props.intl.formatMessage({id: 'apps.allAppsAppPicker.kaizen.tryAgainLink'})}
			</a>
			<span>
				{this.props.intl.formatMessage({id: 'apps.allAppsAppPicker.kaizen.or'})}
			</span>
			<span role='link' tabIndex={0} className={KaizenViewScss.helpLink} onClick={this.props.onGetHelp}
				onKeyDown={(e) => {e.keyCode === 13 && this.props.onGetHelp();}}>
				{this.props.intl.formatMessage({id: 'apps.allAppsAppPicker.kaizen.helpLink'})}
			</span>
		</div>
	);

	renderMessageBox = () => {
		const messageBoxes = [];
		this.props.messagesList.forEach((message) => {
			messageBoxes.push(
				<MessageBox
					id={message.variant}
					className={this.state.showToasts ? '' : 'invisible'}
					variant={message.variant}
					onAction={message.callback}
					appDisplayName={this.productDetails.name}
					intl={this.props.intl}
					key={message.variant}
					handleAnalyticsEvent = {this.props.handleAnalyticsEvent}
				/>
			);
		});
		if (messageBoxes.length !== 0) {
			return <React.Fragment>{messageBoxes}</React.Fragment>;
		}
		return false;
	}

	renderHeaderIcon = () => {
		if ([DOWNLOAD_CONTROLLER_USER_SOURCE.LOGGED_OUT_CAMPAIGN_PAGE, DOWNLOAD_CONTROLLER_USER_SOURCE.SINGLE_APP_POST_PURCHASE_PAGE].includes(this.props.source) && this.productDetails.sapCode !== 'KCCC') {
			const productData = AppsUtil.getFFCProductConfigBySapCode(this.productDetails.sapCode);
			const icon = AppsUtil.getProductIcon(productData);
			return icon ? (<img alt={this.productDetails.name} src={icon} />) : [];
		}
		return (
			<div className={KaizenViewScss.downloadIconContainer} onClick={this.onRestartDownload}>
				<DownloadIcon size='XXL' className={KaizenViewScss.downloadIcon}/>
			</div>
		);
	}

	render() {
		return (
			<div className={`${KaizenViewScss.kaizenView} ${this.props.className || ''}`}>
				{this.renderHeaderIcon()}
				{this.renderHeaderText()}
				{this.renderBodyText()}
				{this.renderMessageBox()}
			</div>
		);
	}
}
